import * as UUID from 'uuid'
import * as React from 'react'
import { FunctionComponent, PropsWithChildren, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import ErrorBoundary from '../components/Diagnostics/ErrorBoundary'
import Router from './Router'
import { FileProvider } from '../context/FileContext'
import { UserProvider } from '../context/UserContext'
import { MessageProvider } from '../context/MessageContext'
import Menu from '../components/Menu'
import Errors from '../components/Diagnostics/Errors'
import Commands from './Commands'
import { MemoryRouter } from 'react-router-dom'
import axios from '../helpers/axiosHelper'
import AxiosContext from '../context/AxiosContext'
import { Route } from 'react-router'
import { css, getTheme, mergeStyleSets, Stack, StackItem } from '@fluentui/react'
import { GlobalProvider } from '../context/GlobalContext'
import { FluentProvider, webLightTheme } from '@fluentui/react-components'
import { AzureWebSocketProvider } from '../context/AzureWebSocketContext'
import { ToastContainer } from 'react-toastify'
import Authentication from '../components/Authentication/Authentication'
import IdentityContext, { IdentityProvider } from '../context/IdentityContext'
import Version from '../components/Diagnostics/Version'
import Support from '../components/Diagnostics/Support'
import FileLink from '../components/Quicklink/FileLink'
import useHost from '../hooks/useHost'
import Loading from '../components/Loading'
import { GenerationQueueProvider } from '../context/QueueContext'
import { MollieProvider } from '../context/MollieContext'
import SupportV9 from '../components/Diagnostics/SupportV9'
import { useTranslation } from 'react-i18next'
import { AppInsightsContext, ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { useBoolean } from '@fluentui/react-hooks'
import { I18NProvider } from '../context/I18NContext'
import PlatformType = Office.PlatformType

const theme = getTheme()

export const appInsightsReact = new ReactPlugin()
export let appInsights = newApplicationInsights()

function newApplicationInsights(): ApplicationInsights {
    return new ApplicationInsights({
        config: {
            connectionString:
                'InstrumentationKey=417924ef-4814-4e9a-880b-69516abc7d57;IngestionEndpoint=https://westeurope-5.in.applicationinsights.azure.com/;LiveEndpoint=https://westeurope.livediagnostics.monitor.azure.com/;ApplicationId=50e2ffce-054d-4094-927f-027fc4b27e89',
            extensions: [appInsightsReact],
            enableAutoRouteTracking: true,
            autoTrackPageVisitTime: true,
        },
    })
}

export const appStyles = mergeStyleSets({
    wrapper: {
        backgroundColor: theme.palette.themeLighterAlt,
        backgroundImage: "url('../images/hammock-bg.png')",
        backgroundSize: '1200px 600px',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        minHeight: '100%',
    },
    container: {
        minWidth: 0,
        maxWidth: 1280,
        backgroundColor: theme.palette.white,
        boxShadow: theme.effects.elevation16,
    },
    header: {
        position: 'sticky',
        top: 0,
        zIndex: 5,
    },
    subMenu: {
        position: 'sticky',
        top: 44,
        zIndex: 7,
        backgroundColor: theme.palette.white,
    },
    actionItems: {
        position: 'sticky',
        top: 88,
        zIndex: 8,
        backgroundColor: theme.palette.white,
    },
    footer: {
        position: 'sticky',
        bottom: 0,
        zIndex: 9,
        backgroundColor: theme.palette.white,
    },
    body: {},
})

const Template: FunctionComponent<PropsWithChildren> = props => {
    const { authenticated } = useContext(IdentityContext)
    return (
        <Stack horizontal horizontalAlign="center" className={css(appStyles.wrapper)}>
            <Stack grow tokens={{ childrenGap: 's1' }} className={css(appStyles.container)}>
                {authenticated && (
                    <Stack className={css(appStyles.header)}>
                        <Menu />
                    </Stack>
                )}
                <Errors />
                <Stack grow className={css(appStyles.body)}>
                    {authenticated ? (
                        <>
                            {props.children}
                            <Commands />
                            <ToastContainer position="bottom-right" />
                        </>
                    ) : (
                        <Authentication />
                    )}
                </Stack>
                <Stack horizontal wrap horizontalAlign="space-between" className={css(appStyles.footer)}>
                    <StackItem>
                        <FileLink />
                    </StackItem>
                    <StackItem>
                        {authenticated && process.env.NODE_ENV === 'development' && <SupportV9 />}
                        {authenticated && <Support />}
                        <Version />
                    </StackItem>
                </Stack>
            </Stack>
        </Stack>
    )
}

const InsightsContainer: FunctionComponent<PropsWithChildren> = props => {
    const host = useHost()
    const { authenticated, user } = useContext(IdentityContext)
    const insightsToBeEnabled = useMemo<boolean>(() => host?.InsightsEnabled || false, [host])
    const [insightsEnabled, { setTrue: setInsightsEnabled, setFalse: setInsightsDisabled }] = useBoolean(false)

    useEffect(() => {
        // Route to the correct base path so that each user gets the correct version served.
        if (host?.ClientPath && window.location.pathname !== host.ClientPath) {
            // Redirect if we're not in the correct environment.
            // window.location.href = host.clientPath + window.location.search + window.location.hash
            const newLocation =
                host.ClientPath + window.location.search + (window.location.hash || '#') + window.location.pathname
            window.location.replace(newLocation)
        }
    }, [host])

    useEffect(() => {
        if (!Office.context || Office.context.platform !== PlatformType.OfficeOnline) {
            if (authenticated && insightsToBeEnabled && !insightsEnabled) {
                appInsights.loadAppInsights()
                setInsightsEnabled()
            } else if ((!authenticated || !insightsToBeEnabled) && insightsEnabled) {
                appInsights.unload()
                setInsightsDisabled()
                appInsights = newApplicationInsights()
            }
        }
    }, [insightsToBeEnabled, authenticated, host])

    useEffect(() => {
        if (!Office.context || Office.context.platform !== PlatformType.OfficeOnline) {
            if (authenticated && user) {
                appInsights.setAuthenticatedUserContext(user.id, host.RowKey, true)
            }
        }
    }, [authenticated, user])

    return insightsToBeEnabled ? (
        <AppInsightsContext.Provider value={appInsightsReact}>{props.children}</AppInsightsContext.Provider>
    ) : (
        props.children
    )
}

const Container: FunctionComponent<PropsWithChildren> = () => {
    const { t: translate } = useTranslation()
    const { authenticated, user } = useContext(IdentityContext)
    const host = useHost()

    useEffect(() => {
        // Route to the correct base path so that each user gets the correct version served.
        if (host?.ClientPath && window.location.pathname !== host.ClientPath) {
            // Redirect if we're not in the correct environment.
            // window.location.href = host.clientPath + window.location.search + window.location.hash
            const newLocation =
                host.ClientPath + window.location.search + (window.location.hash || '#') + window.location.pathname
            window.location.replace(newLocation)
        }
    }, [host])

    if (host?.ClientPath && window.location.pathname !== host.ClientPath) {
        return <Loading label={translate('authentication.host.redirect')} />
    }

    if (!authenticated) {
        return (
            <Template>
                <Authentication />
            </Template>
        )
    }

    return (
        <MemoryRouter initialEntries={[window.location.hash.substring(1)]}>
            <GlobalProvider>
                <GenerationQueueProvider>
                    <FileProvider>
                        <MessageProvider>
                            <UserProvider userId={user.id} userEmail={user.email}>
                                <MollieProvider>
                                    <AzureWebSocketProvider>
                                        <Template>
                                            <Route path="/" component={Router} />
                                        </Template>
                                    </AzureWebSocketProvider>
                                </MollieProvider>
                            </UserProvider>
                        </MessageProvider>
                    </FileProvider>
                </GenerationQueueProvider>
            </GlobalProvider>
        </MemoryRouter>
    )
}

export default function App(): ReactElement {
    const [currentVersion] = useState(process.env.COMMITHASH)
    const [currentVersionTimeout, setCurrentVersionTimeout] = useState<NodeJS.Timeout>(undefined)

    const fetchNewVersion = () => {
        const commitHashUrl =
            window.location.origin +
            (window.location.pathname + '/COMMITHASH').replaceAll('//COMMITHASH', '/COMMITHASH')

        fetch(commitHashUrl, { cache: 'no-cache', headers: { 'Cache-Control': 'no-cache' } })
            .then(response => {
                if (response.ok) {
                    return response.text()
                } else {
                    console.log('Could not load /COMMITHASH: status: ' + response.status)
                    return currentVersion
                }
            })
            .then(response => (UUID.validate(response) ? response : currentVersion))
            .then((newVersion: string) => {
                console.log(`Update check currentVersion=${currentVersion} , newVersion=${newVersion}`)
                if (currentVersion !== newVersion) {
                    window.location.reload()
                } else {
                    setCurrentVersionTimeout(setTimeout(() => fetchNewVersion(), 3600000))
                }
            })
            .catch(error => console.log(error))
    }

    useEffect(() => {
        fetchNewVersion()

        return () => currentVersionTimeout && clearTimeout(currentVersionTimeout)
    }, [])

    return (
        <FluentProvider theme={webLightTheme}>
            <I18NProvider>
                <ErrorBoundary>
                    <AxiosContext.Provider value={axios}>
                        <IdentityProvider>
                            <InsightsContainer>
                                <Container />
                            </InsightsContainer>
                        </IdentityProvider>
                    </AxiosContext.Provider>
                </ErrorBoundary>
            </I18NProvider>
        </FluentProvider>
    )
}
